import { useCallback, useEffect, useRef, useState } from 'react';

// Browser-native voice via the Web Speech API. No backend dependency.
//   - TTS: window.speechSynthesis (Chrome / Edge / Safari)
//   - STT: window.SpeechRecognition / webkitSpeechRecognition (Chrome / Edge)
// Bill (ceo-coach) has voice: 'onyx' on the Mastra agent for future server-side
// audio, but for now we use the browser API — instant, free, no streaming bugs.

type Listener = (finalText: string) => void;

interface UseVoiceOptions {
  onTranscript?: Listener;
  preferredVoiceMatch?: RegExp;
}

interface UseVoiceReturn {
  ttsSupported: boolean;
  sttSupported: boolean;
  speaking: boolean;
  listening: boolean;
  speak: (text: string) => void;
  cancelSpeak: () => void;
  startListening: () => void;
  stopListening: () => void;
}

function pickVoice(preferred?: RegExp): SpeechSynthesisVoice | undefined {
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return undefined;
  if (preferred) {
    const match = voices.find((v) => preferred.test(v.name));
    if (match) return match;
  }
  // Prefer an English male voice that resembles "onyx" tone.
  const enMale = voices.find((v) => /en[-_]/i.test(v.lang) && /male|daniel|fred|alex|onyx/i.test(v.name));
  if (enMale) return enMale;
  return voices.find((v) => /en[-_]/i.test(v.lang)) ?? voices[0];
}

export function useVoice(opts: UseVoiceOptions = {}): UseVoiceReturn {
  const { onTranscript, preferredVoiceMatch } = opts;
  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  // Chrome exposes SpeechRecognition under webkit prefix on most platforms.
  const SR: any = typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : undefined;
  const sttSupported = !!SR;

  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);

  const recognitionRef = useRef<any>(null);

  // Keep latest callback without retriggering recognition setup.
  const onTranscriptRef = useRef<Listener | undefined>(onTranscript);
  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  // Warm up voice list (Chrome lazy-loads voices).
  useEffect(() => {
    if (!ttsSupported) return;
    const handler = () => { /* triggers re-pick on next speak */ };
    window.speechSynthesis.addEventListener('voiceschanged', handler);
    // Trigger initial load.
    window.speechSynthesis.getVoices();
    return () => window.speechSynthesis.removeEventListener('voiceschanged', handler);
  }, [ttsSupported]);

  const speak = useCallback((text: string) => {
    if (!ttsSupported || !text) return;
    const utterance = new SpeechSynthesisUtterance(text);
    const voice = pickVoice(preferredVoiceMatch);
    if (voice) utterance.voice = voice;
    utterance.rate = 1.05;
    utterance.pitch = 0.95;
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  }, [ttsSupported, preferredVoiceMatch]);

  const cancelSpeak = useCallback(() => {
    if (!ttsSupported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [ttsSupported]);

  const startListening = useCallback(() => {
    if (!sttSupported) return;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {/* noop */}
    }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = navigator.language?.startsWith('fr') ? 'fr-CA' : 'en-US';
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.onresult = (event: any) => {
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) finalText += result[0].transcript;
      }
      if (finalText) onTranscriptRef.current?.(finalText.trim());
    };
    recognitionRef.current = rec;
    rec.start();
  }, [SR, sttSupported]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;
    try { recognitionRef.current.stop(); } catch {/* noop */}
    setListening(false);
  }, []);

  // Cleanup on unmount.
  useEffect(() => () => {
    if (ttsSupported) window.speechSynthesis.cancel();
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {/* noop */}
    }
  }, [ttsSupported]);

  return { ttsSupported, sttSupported, speaking, listening, speak, cancelSpeak, startListening, stopListening };
}
